import React, { useState, useEffect, useRef } from "react";
import { Table, Input, Space, Spin, Modal, Form } from "antd";
import { message } from "../../components/antdMessageBridge";
import CommonObj from "../../CommonObj";
import { getUTCDate } from "../../components/utcDate";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useAuthToken } from "../../components/usecontext/AuthContext";
import { useAuth } from "../../auth/AuthContext";
import Html5QrcodePlugin from "../qrscanner/qrscanner";
import "../books/books.css";
import "./verificationqueue.css";
import ManifestAcceptaneIcon from "../../assets/img/svg/xmanifestacceptanceicon2.svg";
import RejectSlide from "./reject-slide";
import { encryptTimestamp } from "../../components/commoncsrf/csrf-common";
import regexService from "../../components/commonregex/regexService";

const Verificationqueue = () => {
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [formAnt] = Form.useForm();
    const [startTime, setStartTime] = useState("");
    const [rejectionOptions, setRejectOptions] = useState([]);
    const { accessToken } = useAuthToken();
    const [activeScanner, setActiveScanner] = useState(null);
    const [form, setForm] = useState({
        bid: ""
    });
    const [cusdectext, setCusDecText] = useState("");
    const [scanimage, setScanImage] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    
    const scanProcessingRef = useRef(false);
    const lastScannedValueRef = useRef("");
    const { roleUserId } = useAuth();
    const [isDisabled, setIsDisabled] = useState(false);
    const [regexLoaded, setRegexLoaded] = useState(false);

    useEffect(() => {
        if (scanimage && cusdectext.trim() !== "") {
            BarcodeScan();
            setScanImage(false);
            setCusDecText("");
            setTimeout(() => {
                scanProcessingRef.current = false;
                lastScannedValueRef.current = "";
            }, 1500);
        }
    }, [scanimage, cusdectext]);

    const handleCancel = () => {
        setIsModalVisible(false);
        setSelectedRecord(null);
        formAnt.resetFields();
    };

    useEffect(() => {
        const loadRegexPatterns = async () => {
            try {
                await regexService.fetchRegexData(accessToken);
                setRegexLoaded(true);
            } catch (error) {
                console.error("Failed to load regex patterns:", error);
            }
        };

        if (accessToken) {
            loadRegexPatterns();
        }
    }, [accessToken]);

    const fetchRejectionData = async () => {
        setLoading(true);
        try {
            const myHeaders = {
                "Authorization": `Bearer ${accessToken}`,
                "X-CSRF-Token": encryptTimestamp(),
                "Content-Type": "application/json",
            };
            const response = await fetch(CommonObj.Endpoints.GetRejectionTypes, {
                method: 'POST',
                headers: myHeaders,
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const result = await response.json();
            const books = Array.isArray(result) ? result : [result];
            const formattedData = books.map(item => ({
                ...item,
                key: item.referenceId,
            }));
            setRejectOptions(formattedData);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const showRejectModal = (record) => {
        setSelectedRowKeys([record]);
        fetchRejectionData();
        setSelectedRecord(record);
        setIsModalVisible(true);
    };

    const showBulkRejectModal = () => {
        setIsDisabled(true); 

        setTimeout(() => {
            setIsDisabled(false);
        }, 3000); 

        const commaSeparatedIds = selectedRowKeys?.join(',');

        if (!commaSeparatedIds) {
            message.error('No rows selected.');
            return;
        }

        fetchRejectionData();
        setSelectedRecord(null);
        setIsModalVisible(true);
    };

    const handleManifestReject = async (key) => {
        setIsDisabled(true); 

        setTimeout(() => {
            setIsDisabled(false);
        }, 3000); 

        let bids;
        
        if (selectedRecord && selectedRecord.bidNo) {
            bids = selectedRecord.bidNo;
        } else if (selectedRowKeys && selectedRowKeys.length > 0) {
            bids = selectedRowKeys.join(',');
        } else {
            message.error('No rows selected.');
            return;
        }

        try {
            const loadingKey = 'rejecting';
            message.loading({ content: 'Rejecting BID...', key: loadingKey });
            const endUTCTime = getUTCDate();
            const payload = {
                RackID: bids,
                userId: roleUserId,
                startTime: startTime,
                endTime: endUTCTime?.UTCDate,
                rejectionType: key.rejectionType,
                rejectionReason: key.rejectionReason
            };

            const myHeaders = {
                "Authorization": `Bearer ${accessToken}`,
                "X-CSRF-Token": encryptTimestamp(),
                'Content-Type': 'application/json'
            };
            
            const response = await fetch(CommonObj.Endpoints.RejectManifest, {
                method: 'POST',
                headers: myHeaders,
                body: JSON.stringify(payload),
            });

            if (response) {
                const startUTCTime = getUTCDate();
                setStartTime(startUTCTime?.UTCDate);
                message.success({ content: 'BID Rejected Successfully!', key: loadingKey, duration: 2 });
                handleCancel();
                setSelectedRowKeys([]);
                setSelectedRecord(null);

                setData(existingData => {
                    return existingData.filter((item) => !bids.includes(item.rackNo));
                });
            } else {
                message.error({ content: 'Failed to Reject BID.', key: loadingKey, duration: 2 });
            }
        } catch (error) {
            console.error('API Error:', error);
            message.error({ content: 'An error occurred. Please try again.', key: 'rejecting', duration: 2 });
        }
    };

    const onSelectChange = (newSelectedRowKeys) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const handleManifestAccept = async (key, mode) => {
        setIsDisabled(true);
        setTimeout(() => setIsDisabled(false), 3000);

        const bids = Array.isArray(key) ? key.join(',') : key;
        
        if (!bids || (Array.isArray(key) && key.length === 0)) {
            message.error('No rows selected.');
            return;
        }
        const loadingKey = 'loading';
        const config = getManifestConfig(mode);
        
        if (!config.URL) return; 

        try {
            message.loading({ content: 'Updating status...', key: loadingKey });

            const response = await fetch(config.URL, {
                method: 'POST',
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "X-CSRF-Token": encryptTimestamp(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    RackID: bids,
                    userId: roleUserId,
                    startTime: startTime,
                    endTime: getUTCDate()?.UTCDate
                }),
            });

            if (!response.ok) throw new Error("API Failure");

            setSelectedRowKeys([]);
            message.success({ content: config.successMsg, key: loadingKey, duration: 2 });
            
            setData(prev => prev.filter(item => !bids.includes(item.rackNo)));

        } catch (error) {
            message.error({ content: 'An error occurred. Please try again.', key: loadingKey, duration: 2 });
        }
    };

    const getManifestConfig = (mode) => {
        const isReject = mode === "reject";
        return {
            URL: isReject ? CommonObj.Endpoints.RejectManifest : CommonObj.Endpoints.AcceptManifest,
            successMsg: isReject ? "Status updated to Manifest Rejected!" : "Status updated to Manifest Accepted!"
        };
    };

    const columns = [
        {
            title: "Rack No",
            dataIndex: "rackNo",
            key: "rackNo",
            width: 510,
        },
        {
            title: "Weight",
            dataIndex: "weight",
            key: "weight",
            width: 400,
        },
        {
            title: "Action",
            key: "action",
            width: 190,
            render: (_, record) => (
                <Space size="middle">
                    <>
                        <CheckCircleOutlined
                            style={{ color: "#002449", cursor: "pointer" }}
                            onClick={() => handleManifestAccept(record.rackNo, "accept")}
                        />
                        <CloseCircleOutlined
                            style={{ color: "#002449", cursor: "pointer" }}
                            onClick={() => showRejectModal(record.rackNo)}
                        />
                    </>
                    {record.status === "Manifest Accept" && (
                        <>
                            <CheckCircleOutlined
                                style={{
                                    color: "#229100",
                                    backgroundColor: "#CAFFD8",
                                    cursor: "pointer",
                                    borderRadius: 9999,
                                }}
                                onClick={() => handleManifestAccept(record.bidNo, "Accepted")}
                            />
                            <CloseCircleOutlined
                                style={{ color: "#002449", cursor: "pointer" }}
                                onClick={() => showRejectModal(record)}
                            />
                        </>
                    )}
                    {record.status === "Manifest Reject" && (
                        <>
                            <CheckCircleOutlined
                                style={{ color: "#002449", cursor: "not-allowed" }}
                            />
                            <CloseCircleOutlined
                                style={{
                                    color: "#EF4444", backgroundColor: "#FFEEEC", cursor: "not-allowed"
                                }}
                            />
                        </>
                    )}
                </Space>
            ),
        },
    ];

    const getRowClassName = (record, index) => {
        return index % 2 === 0 ? "odd-row" : "even-row";
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        preserveSelectedRowKeys: true,
        getCheckboxProps: (record) => ({
            disabled: record.status === 'Manifest Reject',
        }),
    };

    if (loading) {
        return (
            <Spin size="large" style={{ margin: "20px auto" }} />
        );
    }

    const BarcodeScan = async () => {
        if (scanimage && cusdectext.trim() !== "") {
            setScanImage(false);
            cusSearch();
        }
    };

    const cusSearch = async () => {
        const bidValue = form?.bid?.trim() || "";

        if (!bidValue) {
            setData([]);
            return;
        }

        if (regexLoaded && !regexService.validateRack(bidValue).isValid) {
            return message.error("Input format invalid: No matching pattern found.");
        }

        setIsProcessing(true);
        try {
            const apiResp = await fetchManifestList(bidValue);
            
            if (apiResp?.error === "No RackID found") {
                return message.warning("Invalid Rack ID. Please retry with different racks");
            }

            handleDataUpdate(apiResp);
            setForm({ ...form, bid: "" });
            
        } catch (err) {
            console.error(err);
        } finally {
            setIsProcessing(false);
        }
    };

    const fetchManifestList = async (bidValue) => {
        const payload = {
            rackName: bidValue,
            pageNo: 1,
            pageSize: 10,
            search: "{}",
            orderByColumn: "CreatedOn",
            orderBy: "DESC"
        };

        const response = await fetch(CommonObj.Endpoints.ManifestAcceptanceList, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
                "X-CSRF-Token": encryptTimestamp()
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) throw new Error("Network response was not ok");
        return response.json();
    };

    const handleDataUpdate = (apiResp) => {
        const currentRackIds = data.map(item => item.rackNo);
        const existingInGrid = apiResp.filter(item => currentRackIds.includes(item.rackNo));
        const newItems = apiResp.filter(item => !currentRackIds.includes(item.rackNo));
        
        const existingIdsString = existingInGrid.map(item => item.rackNo).join(",");

        setData(prev => [...prev, ...newItems]);

        if (newItems.length > 0) {
            const label = newItems.length === 1 ? "Rack" : "Racks";
            message.success(`${newItems.length} ${label} added successfully!`);
        }

        if (existingIdsString) {
            message.error(`"${existingIdsString}" already Exist!`);
        }
    };

    const onNewScanResult = (decodedText) => {
        if (scanProcessingRef.current || lastScannedValueRef.current === decodedText) {
            return;
        }
        scanProcessingRef.current = true;
        lastScannedValueRef.current = decodedText;
        setCusDecText(decodedText);
        setForm({ ...form, bid: decodedText });
        setScanImage(true);

        setActiveScanner(null);
    };

    return (
        <>
            <Modal
                title={`Scan ${activeScanner ? activeScanner : ""}`}
                open={!!activeScanner}
                onCancel={() => setActiveScanner(null)}
                footer={null}
                destroyOnClose
                className="scan-model-container"
                width={1000}
            >
                <div className="scan-modal">
                    <Html5QrcodePlugin
                        fps={30}
                        qrbox={{ width: 1000, height: 400 }}
                        aspectRatio={2.8}
                        disableFlip={false}
                        qrCodeSuccessCallback={onNewScanResult}
                    />
                </div>
            </Modal>
            <main className="book-page-main-container">
                <div className="book-page-container">
                    <div className="book-page-title-container">
                        <div className="book-page-title">
                            <span>
                                <img src={ManifestAcceptaneIcon} alt="Manifest Acceptance Icon"></img>
                            </span>
                            <h2>Manifest Acceptance</h2>
                        </div>
                    </div>
                    <div className="book-page-form withGrid">
                        <div className="book-page-camera-button-container multiple">
                            <Input
                                placeholder=""
                                value={form.bid}
                                className={`${form.bid ? "has-id" : ""}`}
                                onChange={(e) => setForm({ ...form, bid: e.target.value })}
                                onPressEnter={() => !isProcessing && cusSearch()}
                                disabled={isProcessing}
                            />
                            {form.bid.length === 0 && (
                                <label>Enter Rack Number / Scan BID Using “<strong>Rack Number</strong>”<span className="form-required">*</span></label>
                            )}
                            <button
                                type="button"
                                className="book-page-camera-button"
                                data-track="manifest-verification-open-rack-scanner"
                                onClick={() => setActiveScanner("Rack")}
                            >
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle
                                        cx="12"
                                        cy="13"
                                        r="3"
                                        stroke="#1C274C"
                                        strokeWidth="1.5"
                                    />
                                    <path
                                        opacity="0.5"
                                        d="M10 19.9999H14C16.8089 19.9999 18.2134 19.9999 19.2223 19.3258C19.659 19.0339 20.034 18.6589 20.3259 18.2222C21 17.2133 21 15.8088 21 12.9999C21 10.191 20.9999 8.78673 20.3257 7.77783C20.0339 7.34107 19.6589 6.96607 19.2221 6.67423C18.2132 6.00011 16.8088 6.00011 13.9999 6.00011H9.99985C7.19093 6.00011 5.78646 6.00011 4.77757 6.67423C4.34081 6.96607 3.96581 7.34107 3.67397 7.77783C3 8.7865 3 10.1903 3 12.998L3 12.9999C3 15.8088 3 17.2133 3.67412 18.2222C3.96596 18.6589 4.34096 19.0339 4.77772 19.3258C5.78661 19.9999 7.19108 19.9999 10 19.9999Z"
                                        stroke="#1C274C"
                                        strokeWidth="1.5"
                                    />
                                    <path
                                        d="M18 10H17.5"
                                        stroke="#1C274C"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                    />
                                    <path
                                        opacity="0.5"
                                        d="M14.5 3.5H9.5"
                                        stroke="#1C274C"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                    />
                                </svg>
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M8.00098 4C8.00098 3.44772 8.41425 3 8.92405 3H15.0779C15.5877 3 16.001 3.44772 16.001 4C16.001 4.55228 15.5877 5 15.0779 5H8.92405C8.41425 5 8.00098 4.55228 8.00098 4Z"
                                        fill="url(#paint0_linear_273_6917)"
                                    />
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M9.77875 21H14.2232C17.3442 21 18.9047 21 20.0257 20.2777C20.511 19.965 20.9277 19.5632 21.252 19.0953C22.001 18.0143 22.001 16.5095 22.001 13.4999C22.001 10.4903 22.0008 8.9857 21.2518 7.90473C20.9275 7.43676 20.5109 7.03497 20.0256 6.72228C18.9046 6 17.3441 6 14.223 6H9.77859C6.65756 6 5.09705 6 3.97605 6.72228C3.49077 7.03497 3.0741 7.43676 2.74984 7.90473C2.00098 8.98547 2.00098 10.4896 2.00098 13.4979L2.00098 13.4999C2.00098 16.5095 2.00098 18.0143 2.75 19.0953C3.07426 19.5632 3.49093 19.965 3.97622 20.2777C5.09721 21 6.65773 21 9.77875 21ZM7.83431 13.4999C7.83431 11.2808 9.69979 9.48196 12.001 9.48196C14.3022 9.48196 16.1676 11.2808 16.1676 13.4999C16.1676 15.7189 14.3022 17.5178 12.001 17.5178C9.69979 17.5178 7.83431 15.7189 7.83431 13.4999ZM9.50098 13.4999C9.50098 12.1685 10.6203 11.0891 12.001 11.0891C13.3817 11.0891 14.501 12.1685 14.501 13.4999C14.501 14.8313 13.3817 15.9106 12.001 15.9106C10.6203 15.9106 9.50098 14.8313 9.50098 13.4999ZM18.1121 9.48196C17.6519 9.48196 17.2788 9.84174 17.2788 10.2855C17.2788 10.7294 17.6519 11.0891 18.1121 11.0891H18.6676C19.1279 11.0891 19.501 10.7294 19.501 10.2855C19.501 9.84174 19.1279 9.48196 18.6676 9.48196H18.1121Z"
                                        fill="url(#paint1_linear_273_6917)"
                                    />
                                    <defs>
                                        <linearGradient
                                            id="paint0_linear_273_6917"
                                            x1="22.601"
                                            y1="11.9503"
                                            x2="1.98296"
                                            y2="14.3301"
                                            gradientUnits="userSpaceOnUse"
                                        >
                                            <stop stopColor="#033D9B" />
                                            <stop offset="1" stopColor="#29ACD5" />
                                        </linearGradient>
                                        <linearGradient
                                            id="paint1_linear_273_6917"
                                            x1="22.601"
                                            y1="11.9503"
                                            x2="1.98296"
                                            y2="14.3301"
                                            gradientUnits="userSpaceOnUse"
                                        >
                                            <stop stopColor="#033D9B" />
                                            <stop offset="1" stopColor="#29ACD5" />
                                        </linearGradient>
                                    </defs>
                                </svg>
                            </button>
                            <button
                                type="button"
                                className="book-page-camera-button-input-2"
                                data-track="manifest-verification-search-rack"
                                onClick={cusSearch}
                                disabled={isProcessing}
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M20 10C20 15.5228 15.5228 20 10 20C4.47715 20 0 15.5228 0 10C0 4.47715 4.47715 0 10 0C15.5228 0 20 4.47715 20 10ZM11.0303 6.46967C10.7374 6.17678 10.2626 6.17678 9.96967 6.46967C9.67678 6.76256 9.67678 7.23744 9.96967 7.53033L12.4393 10L9.96967 12.4697C9.67678 12.7626 9.67678 13.2374 9.96967 13.5303C10.2626 13.8232 10.7374 13.8232 11.0303 13.5303L14.0303 10.5303C14.3232 10.2374 14.3232 9.76256 14.0303 9.46967L11.0303 6.46967ZM5.96967 6.46967C6.26256 6.17678 6.73744 6.17678 7.03033 6.46967L10.0303 9.46967C10.3232 9.76256 10.3232 10.2374 10.0303 10.5303L7.03033 13.5303C6.73744 13.8232 6.26256 13.8232 5.96967 13.5303C5.67678 13.2374 5.67678 12.7626 5.96967 12.4697L8.43934 10L5.96967 7.53033C5.67678 7.23744 5.67678 6.76256 5.96967 6.46967Z"
                                        fill="url(#paint0_linear_1900_8687)"
                                    />
                                    <defs>
                                        <linearGradient
                                            id="paint0_linear_1900_8687"
                                            x1="20.6"
                                            y1="9.94475"
                                            x2="-0.0696504"
                                            y2="12.0919"
                                            gradientUnits="userSpaceOnUse"
                                        >
                                            <stop stopColor="#033D9B" />
                                            <stop offset="1" stopColor="#29ACD5" />
                                        </linearGradient>
                                    </defs>
                                </svg>
                            </button>
                        </div>
                    </div>
                    <div className="table-container reducedbottom-padding">
                        <div className="rounded-table-wrapper">
                            <Table
                                rowSelection={rowSelection}
                                columns={columns}
                                dataSource={data}
                                className="common-grid withScanner"
                                pagination={false}
                                rowKey={(record) => record.rackNo}
                                scroll={{ x: "max-content", y: "calc(100vh - 390px)" }}
                                rowClassName={getRowClassName}
                            />
                        </div>
                        <RejectSlide isModalVisible={isModalVisible} rejectionOptions={rejectionOptions} handleCancel={handleCancel} selectedRowKeys={selectedRowKeys} handleManifestReject={handleManifestReject} isDisabled={isDisabled} className="story-slider slider-with-footer" width={540} />
                    </div>
                    <div className="book-page-footer-container">
                        <div className="book-page-action-container">
                            <div></div>
                            <div className="book-page-buttons-container">
                                <button disabled={isDisabled} onClick={showBulkRejectModal} className="reject-button">Reject</button>
                                <button disabled={isDisabled} onClick={() => handleManifestAccept(selectedRowKeys, "accept")} className="accept-button">Accept</button>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default Verificationqueue;
